// Renders norm-violation outlines + a DOM tooltip over the Konva stage.
//
// The layer is purely cosmetic (listening:false) — the original object rect
// in objectLayer still receives clicks, drags, transforms. Tooltip is a
// plain <div> positioned absolute; Konva's stage-level mousemove decides
// which violating object is under the pointer.

import Konva from "konva"
import { rectCorners } from "./shadows"

const STROKE_ERROR = "#dc2626"    // red-600
const STROKE_WARN  = "#f59e0b"    // amber-500

export class ViolationRenderer {
  constructor({ stage, layer, container, metresToPx }) {
    this.stage = stage
    this.layer = layer
    this.container = container
    this.metresToPx = metresToPx
    this.byIdMap = new Map()
    this.currentHoverId = null

    this.tooltip = document.createElement("div")
    this.tooltip.setAttribute("role", "tooltip")
    this.tooltip.className =
      "pointer-events-none absolute z-30 hidden max-w-xs rounded bg-neutral-900/95 " +
      "px-2.5 py-2 text-xs leading-snug text-white shadow-lg"
    this.container.appendChild(this.tooltip)

    this._onMove = (e) => this.#handleMove(e)
    this._onLeave = () => this.#hideTooltip()
    stage.on("mousemove", this._onMove)
    stage.on("mouseleave", this._onLeave)
  }

  destroy() {
    this.stage.off("mousemove", this._onMove)
    this.stage.off("mouseleave", this._onLeave)
    this.tooltip.remove()
  }

  // violations: array of violation objects from norms.validate()
  // byIdMap:    Map<objectId, violation[]>
  // objects:    current editorStore.objects
  // scale:      { pxPerMetre, offsetPx }
  render({ violations, byIdMap, objects, scale }) {
    this.layer.destroyChildren()
    this.byIdMap = byIdMap
    this.objects = objects
    this.scale = scale

    if (!violations || violations.length === 0 || !scale) {
      this.layer.batchDraw()
      this.#hideTooltip()
      return
    }

    const byObj = new Map()
    for (const [id, vs] of byIdMap) {
      // Pick the strictest severity for outline colour.
      const hasError = vs.some((v) => v.severity === "error")
      byObj.set(id, hasError ? "error" : "warning")
    }

    for (const obj of objects) {
      const sev = byObj.get(obj.id)
      if (!sev) continue
      const corners = rectCorners(obj)
      const points = corners.flatMap(([xM, yM]) => {
        const p = this.metresToPx({ xM, yM }, scale)
        return [p.x, p.y]
      })
      const stroke = sev === "error" ? STROKE_ERROR : STROKE_WARN
      this.layer.add(new Konva.Line({
        points, closed: true,
        stroke, strokeWidth: 3, dash: [6, 4],
        listening: false, perfectDrawEnabled: false,
      }))
    }
    this.layer.batchDraw()

    // If we were showing a tooltip for an object that no longer violates,
    // clear it.
    if (this.currentHoverId && !byIdMap.has(this.currentHoverId)) {
      this.#hideTooltip()
    }
  }

  #handleMove() {
    if (!this.stage || !this.byIdMap || this.byIdMap.size === 0) {
      this.#hideTooltip()
      return
    }
    // Konva's built-in hit test covers the actual object rect in objectLayer.
    // We look up its id and match against the violation map.
    const target = this.stage.getIntersection(this.stage.getPointerPosition())
    if (!target) { this.#hideTooltip(); return }
    const id = target.id && target.id()
    if (!id || !this.byIdMap.has(id)) { this.#hideTooltip(); return }
    this.#showTooltip(id)
  }

  #showTooltip(id) {
    const violations = this.byIdMap.get(id) ?? []
    if (violations.length === 0) { this.#hideTooltip(); return }
    if (this.currentHoverId !== id) {
      this.tooltip.innerHTML = ""
      for (const v of violations) {
        const row = document.createElement("div")
        row.className = "mb-1 last:mb-0"
        const title = document.createElement("div")
        title.className = "font-semibold"
        title.textContent = v.title
        row.appendChild(title)
        if (v.distanceM != null && v.minDistanceM != null) {
          const dist = document.createElement("div")
          dist.className = "text-xs text-neutral-200"
          dist.textContent = `текущее: ${v.distanceM.toFixed(2)} м · требуется: ≥${v.minDistanceM} м`
          row.appendChild(dist)
        }
        const ref = document.createElement("div")
        ref.className = "text-xs text-neutral-300"
        ref.textContent = v.normRef
        row.appendChild(ref)
        this.tooltip.appendChild(row)
      }
      this.currentHoverId = id
    }
    const pos = this.stage.getPointerPosition()
    if (!pos) return
    // Offset so cursor doesn't sit on top of the tooltip (would flicker).
    const offsetX = 14, offsetY = 14
    const containerRect = this.container.getBoundingClientRect()
    let x = pos.x + offsetX
    let y = pos.y + offsetY
    // Keep inside container: right edge, bottom edge.
    this.tooltip.classList.remove("hidden")
    const ttRect = this.tooltip.getBoundingClientRect()
    if (x + ttRect.width > containerRect.width) x = Math.max(0, containerRect.width - ttRect.width - 4)
    if (y + ttRect.height > containerRect.height) y = Math.max(0, pos.y - ttRect.height - offsetY)
    this.tooltip.style.left = `${x}px`
    this.tooltip.style.top = `${y}px`
  }

  #hideTooltip() {
    if (this.currentHoverId == null) return
    this.currentHoverId = null
    this.tooltip.classList.add("hidden")
  }
}
